// Human-friendly relative time formatter for listing cards.
//
// Goals:
//  - Freshness signal for users browsing listings
//  - Gracefully degrade for old posts (no "Posted 2 years ago" embarrassment)
//  - Safe for missing / invalid inputs (returns empty string)
//
// Usage in templates:
//    {{ formatRelativeTime(service.createdTime) }}  →  "Posted 2 hours ago"
//    isNewPost(service.createdTime)                 →  true for < 24h old

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Returns a short relative-time string suitable for listing cards.
 * Caps at "this month" — anything older shows an absolute date instead.
 *
 * @param {string|Date|number} input - ISO string, Date, or epoch ms
 * @returns {string}
 */
export function formatRelativeTime(input) {
  if (!input) return "";
  const date = input instanceof Date ? input : new Date(input);
  if (isNaN(date.getTime())) return "";

  const diff = Date.now() - date.getTime();

  // Future dates (clock skew, seeded data) — treat as "just now"
  if (diff < 0) return "Just now";

  if (diff < MINUTE) return "Just now";

  if (diff < HOUR) {
    const mins = Math.floor(diff / MINUTE);
    return `Posted ${mins} min${mins === 1 ? "" : "s"} ago`;
  }

  if (diff < DAY) {
    const hours = Math.floor(diff / HOUR);
    return `Posted ${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  const days = Math.floor(diff / DAY);
  if (days === 1) return "Posted yesterday";
  if (days < 7) return `Posted ${days} days ago`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `Posted ${weeks} week${weeks === 1 ? "" : "s"} ago`;
  }

  // For anything older than 30 days, show an absolute short date so
  // the card never shows "Posted 2 years ago" — which erodes trust.
  return `Posted ${date.toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  })}`;
}

/**
 * Returns true if the post is fresh enough to display a "New" badge.
 * Threshold: 24 hours.
 *
 * @param {string|Date|number} input
 * @returns {boolean}
 */
export function isNewPost(input) {
  if (!input) return false;
  const date = input instanceof Date ? input : new Date(input);
  if (isNaN(date.getTime())) return false;
  return Date.now() - date.getTime() < DAY;
}
